import React, { useState, useEffect, useCallback } from 'react';

import { useStyles } from '../../../Sessions/SessionsParametersDialog/SessionsParametersCardLimit/SessionsParametersCardLimit.styles';
import {
  CardLimits,
  ExaminingTeamMembers,
  UpdateCardLimitsResponse,
} from '../SessionsParametersDialogContent/SessionsParametersDialogContent.types';
import {
  CardLimitsData,
  SessionsParametersKeyExaminer,
} from '../../SessionsParametersDialog/SessionsParametersCardLimit/SessionsParametersCardLimit.types';

import { saveCardLimits } from '../../../../services/exams/sessions/sessions.service';
import { SessionsParametersCardLimitCellPerExaminer } from './SessionsParametersCardLimitCellPerExaminer.component';
import { getChangesFromArrayOfObject } from '../../../../utils/getChangesFromArrayOfObject';
import { InputTable } from '../../../../components/InputTable/InputTable.component';
import { useLayoutContext } from '../../../../Context/Layout.context';
import { ErrorList, InputTableColumns } from '../../../../components/InputTable/InputTable.type';
import { AxiosResponse } from 'axios';
import { getValidationMessage } from '../SessionsParametersCardLimit/helpers';

interface SessionsParametersCardLimitProps {
  apiResponse: string;
  items: ExaminingTeamMembers[];
  defaultCardLimit: CardLimits;
  onClose: () => void;
  reload: () => void;
  selectedTaskGroupId: string;
}

const columns: InputTableColumns[] = [
  {
    id: 'fullName',
    label: 'Egzaminator',
  },
  {
    id: 'limitTotal',
    label: 'Docelowa liczba rozwiązań GZ',
  },
  {
    id: 'limitDaily',
    label: 'Dobowa liczba rozwiązań GZ',
  },
  {
    id: 'limitDesk',
    label: (
      <div>
        Jednoczesna dopuszczalna
        <br /> liczba rozwiązań GZ
      </div>
    ),
  },
];

export const SessionsParametersCardLimitPerExaminer: React.FC<SessionsParametersCardLimitProps> = ({
  items,
  apiResponse,
  defaultCardLimit,
  reload,
  onClose,
  selectedTaskGroupId,
}) => {
  const [parameters, setParameters] = useState<ExaminingTeamMembers[]>(items);
  const [invalidType, setInvalidType] = useState<ErrorList[]>([]);
  const [edited, setEdited] = useState<boolean>(false);
  const styles = useStyles();

  const { openAlert } = useLayoutContext();

  useEffect(() => {
    setParameters(items);
  }, [items]);

  const validateLimitTotal = (value: number, limits: ExaminingTeamMembers): boolean => {
    if (!limits.limitDaily && !limits.limitDesk) {
      return true;
    }

    if (limits.limitDaily && value && value < limits.limitDaily) {
      return false;
    }

    if (limits.limitDesk && value && value < limits.limitDesk) {
      return false;
    }

    return true;
  };

  const validateLimitDaily = (value: number, limits: ExaminingTeamMembers): boolean => {
    if (!limits.limitTotal && !limits.limitDesk) {
      return true;
    }

    if (limits.limitDesk && value && value < limits.limitDesk) {
      return false;
    }

    if (limits.limitTotal && value && value > limits.limitTotal) {
      return false;
    }

    if (limits.limitDesk && limits.limitDesk > limits.limitTotal) {
      return false;
    }

    return true;
  };

  const validateLimitDesk = (value: number, limits: ExaminingTeamMembers): boolean => {
    if (!limits.limitTotal && !limits.limitDaily) {
      return true;
    }

    if (limits.limitTotal && limits.limitDaily > limits.limitTotal) {
      return false;
    }

    if (limits.limitTotal && value && value > limits.limitTotal) {
      return false;
    }

    if (limits.limitDaily && value && value > limits.limitDaily) {
      return false;
    }
    return true;
  };

  const valueValidation = useCallback(
    (type: SessionsParametersKeyExaminer, value: number, limits: ExaminingTeamMembers): boolean => {
      if (type === 'limitTotal') {
        const isValid = validateLimitTotal(value, limits);
        if (isValid) {
          setInvalidType((prev) => prev.filter((e) => e.id !== limits.examinerId));
        } else {
          setInvalidType((prev) => [...prev, { type, id: limits.examinerId }]);
        }
        return isValid;
      } else if (type === 'limitDaily') {
        const isValid = validateLimitDaily(value, limits);
        if (isValid) {
          setInvalidType((prev) => prev.filter((e) => e.id !== limits.examinerId));
        } else {
          setInvalidType((prev) => [...prev, { type, id: limits.examinerId }]);
        }
        return isValid;
      } else if (type === 'limitDesk') {
        const isValid = validateLimitDesk(value, limits);
        if (isValid) {
          setInvalidType((prev) => prev.filter((e) => e.id !== limits.examinerId));
        } else {
          setInvalidType((prev) => [...prev, { type, id: limits.examinerId }]);
        }
        return isValid;
      } else {
        setInvalidType((prev) => [...prev]);
        return true;
      }
    },
    [],
  );

  const handleValueCallback = useCallback(
    (key: SessionsParametersKeyExaminer, value: number, id: string | number) => {
      setParameters((state) => {
        return state.map((s) => {
          if (s.examinerId === id) {
            s[key] = value as never;
          }

          return s;
        });
      });
    },
    [setParameters],
  );

  const handleSwitchCallback = useCallback(
    (key: SessionsParametersKeyExaminer, value: boolean, id: string | number) => {
      setParameters((state) => {
        return state.map((s: ExaminingTeamMembers) => {
          if (s.examinerId === id) {
            if (key !== 'fullName') {
              s[key] = (value ? (defaultCardLimit[key] !== null ? defaultCardLimit[key]! : null) : null) as never;
            }
          }
          return s;
        });
      });
    },
    [defaultCardLimit],
  );

  useEffect(() => {
    setEdited(JSON.stringify(parameters) !== apiResponse);
  }, [parameters, apiResponse]);

  const handleDiscardCallback = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleSaveCallback = useCallback(() => {
    const apiData: ExaminingTeamMembers[] = JSON.parse(apiResponse);
    const filterData: ExaminingTeamMembers[] = getChangesFromArrayOfObject<ExaminingTeamMembers>(apiData, parameters);
    const data: CardLimitsData = {
      cardLimits: filterData.map((p) => {
        const apiDataLimitDesk = apiData.filter((e) => e.id === p.id)[0].limitDesk;
        const apiDataLimitDaily = apiData.filter((e) => e.id === p.id)[0].limitDaily;
        const apiDataLimitTotal = apiData.filter((e) => e.id === p.id)[0].limitTotal;
        const apiDataExtendedAccessDuration = apiData.filter((e) => e.id === p.id)[0].extendedAccessDuration;
        return {
          examTaskGroupId: selectedTaskGroupId,
          examinerPermissionId: p.examinerPermissionId,
          limitDesk: p.limitDesk !== apiDataLimitDesk ? p.limitDesk : apiDataLimitDesk,
          limitDaily: p.limitDaily !== apiDataLimitDaily ? p.limitDaily : apiDataLimitDaily,
          limitTotal: p.limitTotal !== apiDataLimitTotal ? p.limitTotal : apiDataLimitTotal,
          extendedAccessDuration:
            p.extendedAccessDuration !== apiData.filter((e) => e.id === p.id)[0].extendedAccessDuration
              ? p.extendedAccessDuration
              : apiDataExtendedAccessDuration,
        };
      }),
    };
    saveCardLimits(data)
      .then((res: AxiosResponse<UpdateCardLimitsResponse>) => {
        if (res.data === UpdateCardLimitsResponse.Success) {
          openAlert('success');
          setEdited(false);
          reload();
        } else {
          openAlert('error');
        }
      })
      .catch(() => {
        openAlert('error');
      });
  }, [parameters, onClose]);

  const getRowComponent = (
    type: string,
    value: ExaminingTeamMembers,
    isValid: boolean,
  ): React.ReactNode | string | null => {
    switch (type) {
      case 'fullName':
        return <p className={styles.textCellExaminer}>{value[type]}</p>;

      case 'limitDaily':
        const intialValueLD = value[type] ?? defaultCardLimit.limitDaily;
        return (
          <SessionsParametersCardLimitCellPerExaminer
            type={type}
            value={value}
            initialValue={intialValueLD}
            defaultValue={defaultCardLimit.limitDaily}
            handleValueChange={handleValueCallback}
            handleSwitchChange={handleSwitchCallback}
            validation={valueValidation}
            isValid={isValid}
            defaultCardLimit={defaultCardLimit}
            examinerFullName={value.fullName}
          />
        );

      case 'limitDesk':
        const intialValueLDE = value[type] ?? defaultCardLimit.limitDesk;
        return (
          <SessionsParametersCardLimitCellPerExaminer
            type={type}
            value={value}
            initialValue={intialValueLDE}
            defaultValue={defaultCardLimit.limitDesk}
            handleValueChange={handleValueCallback}
            handleSwitchChange={handleSwitchCallback}
            validation={valueValidation}
            isValid={isValid}
            defaultCardLimit={defaultCardLimit}
            examinerFullName={value.fullName}
          />
        );

      case 'limitTotal':
        const intialValueLT = value[type] ?? defaultCardLimit.limitTotal;
        return (
          <SessionsParametersCardLimitCellPerExaminer
            type={type}
            value={value}
            initialValue={intialValueLT}
            defaultValue={defaultCardLimit.limitTotal}
            handleValueChange={handleValueCallback}
            handleSwitchChange={handleSwitchCallback}
            validation={valueValidation}
            isValid={isValid}
            defaultCardLimit={defaultCardLimit}
            examinerFullName={value.fullName}
          />
        );
      default:
        return null;
    }
  };

  const emptyMessage = 'Brak wyników';

  return (
    <InputTable<ExaminingTeamMembers>
      columns={columns}
      parameters={parameters}
      items={items}
      keyParam={'examinerId'}
      invalidType={invalidType}
      headerText={'Zdefiniuj limity kart i dostęp'}
      onCancel={handleDiscardCallback}
      onSave={handleSaveCallback}
      getValidationMessage={getValidationMessage}
      getRowComponent={getRowComponent}
      edited={edited}
      emptyMessage={emptyMessage}
    />
  );
};
